import { fetchFastApi } from "../../api/fastapi";
import { useHookCacheQuery } from "../../lib/hookCache";
import {
  ControlWithProcedure,
  ControlWithProcedureSchema,
} from "../../models/audit/control";

/**
 * Hook for the heavy-control endpoint: `GET /controls/v2/{controlId}`.
 *
 * The control-session envelope serializes only `DryControl` for
 * `controlSession.control` — it does not carry the hydrated `attributes` or
 * `keyReportItems` collections, and neither does the per-procedure
 * table-shell. So this hook is the only source for those fields, and callers
 * that need them go through it. The call retires once the envelope's
 * `control` field carries them.
 *
 * Accepts `null` because the widened session envelope carries
 * `controlId: string | null` (null for work-step-headed sessions); the
 * `enabled` guard below keeps the query off until a real id exists, so
 * callers see `data === undefined` — the same shape as the loading state.
 */
export function useControl(controlId: string | null | undefined) {
  return useHookCacheQuery<ControlWithProcedure>({
    key: ["control", controlId ?? ""],
    fetcher: async () => {
      const response = await fetchFastApi({
        method: "GET",
        path: `/controls/v2/${controlId}`,
      });
      return ControlWithProcedureSchema.parse(response.control);
    },
    enabled: !!controlId,
  });
}
